import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { Copy, Trash2, Clock, RefreshCw, Link2, Mail, Eye, EyeOff, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import './Shares.css';

interface Share {
  id: string;
  shareToken: string;
  shareType: 'link' | 'email';
  email?: string;
  resourceType?: 'credential' | 'kubeconfig' | 'vpn';
  credential?: {
    id: string;
    name: string;
    description?: string;
    category: string;
    type: string;
  };
  kubeconfig?: {
    id: string;
    name: string;
    description?: string;
    clusterName?: string;
    contextName?: string;
  };
  vpn?: {
    id: string;
    name: string;
    description?: string;
    serverAddress?: string;
    protocol?: string;
  };
  isOneTimeUse: boolean;
  isUsed: boolean;
  expiresAt: string | null;
  accessedAt?: string;
  createdAt: string;
  shareUrl: string;
  hasPassword: boolean;
}

const Shares: React.FC = () => {
  const queryClient = useQueryClient();
  const [extendingShare, setExtendingShare] = useState<string | null>(null);
  const [extendHours, setExtendHours] = useState(24);

  const { data, isLoading } = useQuery(
    'my-shares',
    async () => {
      const response = await api.get('/shares/my-shares');
      return response.data.shares as Share[];
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  const deleteMutation = useMutation(
    async (shareId: string) => {
      await api.delete(`/shares/shares/${shareId}`);
    },
    {
      onSuccess: () => {
        toast.success('Share revoked successfully');
        queryClient.invalidateQueries('my-shares');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to revoke share');
      },
    }
  );

  const extendMutation = useMutation(
    async ({ shareId, hours }: { shareId: string; hours: number }) => {
      const response = await api.patch(`/shares/shares/${shareId}`, { expiresInHours: hours });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Share expiration updated successfully');
        setExtendingShare(null);
        queryClient.invalidateQueries('my-shares');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update share expiration');
      },
    }
  );

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDelete = (shareId: string, resourceName: string) => {
    if (window.confirm(`Are you sure you want to revoke the share for "${resourceName}"?`)) {
      deleteMutation.mutate(shareId);
    }
  };

  const handleExtend = (shareId: string) => {
    extendMutation.mutate({ shareId, hours: extendHours });
  };

  const getTimeRemaining = (expiresAt: string | null) => {
    if (!expiresAt) return 'Never expires';

    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const isExpiringSoon = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    return diff > 0 && diff < 24 * 60 * 60 * 1000; // Less than 24 hours
  };

  if (isLoading) {
    return <div className="loading">Loading shares...</div>;
  }

  const shares = data || [];

  return (
    <div className="shares-page">
      <div className="page-header">
        <h1>Active Shares</h1>
        <p className="subtitle">Manage and monitor all your active shares (credentials, kubeconfigs, and VPN configs)</p>
      </div>

      {shares.length === 0 ? (
        <div className="empty-state">
          <Link2 size={48} className="empty-icon" />
          <h2>No Active Shares</h2>
          <p>You don't have any active shares at the moment.</p>
          <p className="hint">Share credentials, kubeconfigs, or VPN configs from their respective pages to see them here.</p>
        </div>
      ) : (
        <div className="shares-grid">
          {shares.map((share) => (
            <div key={share.id} className={`share-card ${isExpiringSoon(share.expiresAt) ? 'expiring-soon' : ''}`}>
              <div className="share-header">
                <div className="share-credential">
                  <h3>{share.credential?.name || share.kubeconfig?.name || share.vpn?.name}</h3>
                  {(share.credential?.description || share.kubeconfig?.description || share.vpn?.description) && (
                    <p className="credential-description">
                      {share.credential?.description || share.kubeconfig?.description || share.vpn?.description}
                    </p>
                  )}
                  <div className="credential-badges">
                    {share.credential ? (
                      <>
                        <span className="badge">{share.credential.category.replace(/_/g, ' ')}</span>
                        <span className="badge">{share.credential.type.replace(/_/g, ' ')}</span>
                      </>
                    ) : share.kubeconfig ? (
                      <>
                        <span className="badge">Kubeconfig</span>
                        {share.kubeconfig.clusterName && <span className="badge">Cluster: {share.kubeconfig.clusterName}</span>}
                        {share.kubeconfig.contextName && <span className="badge">Context: {share.kubeconfig.contextName}</span>}
                      </>
                    ) : (
                      <>
                        <span className="badge">VPN</span>
                        {share.vpn?.protocol && <span className="badge">{share.vpn.protocol}</span>}
                        {share.vpn?.serverAddress && <span className="badge">{share.vpn.serverAddress}</span>}
                      </>
                    )}
                  </div>
                </div>
                <div className="share-type-icon">
                  {share.shareType === 'email' ? (
                    <Mail size={24} className="email-icon" />
                  ) : (
                    <Link2 size={24} className="link-icon" />
                  )}
                </div>
              </div>

              <div className="share-info">
                <div className="info-row">
                  <span className="info-label">Type:</span>
                  <span className="info-value">{share.shareType === 'email' ? 'Email Invitation' : 'Direct Link'}</span>
                </div>
                {share.email && (
                  <div className="info-row">
                    <span className="info-label">Recipient:</span>
                    <span className="info-value">{share.email}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Status:</span>
                  <span className={`status-badge ${share.isUsed ? 'used' : 'active'}`}>
                    {share.isUsed ? 'Used' : 'Active'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">One-time use:</span>
                  <span className="info-value">{share.isOneTimeUse ? 'Yes' : 'No'}</span>
                </div>
                {share.hasPassword && (
                  <div className="info-row">
                    <span className="info-label">Password:</span>
                    <span className="info-value">Protected</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Expires:</span>
                  <span className={`info-value ${share.expiresAt && isExpiringSoon(share.expiresAt) ? 'expiring' : ''}`}>
                    {share.expiresAt ? new Date(share.expiresAt).toLocaleString() : 'Never'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Time remaining:</span>
                  <span className={`info-value ${isExpiringSoon(share.expiresAt) ? 'expiring' : ''}`}>
                    {getTimeRemaining(share.expiresAt)}
                  </span>
                </div>
                {share.accessedAt && (
                  <div className="info-row">
                    <span className="info-label">Last accessed:</span>
                    <span className="info-value">{new Date(share.accessedAt).toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="share-url-section">
                <label>Share Link</label>
                <div className="url-input-group">
                  <input type="text" value={share.shareUrl} readOnly className="share-url-input" />
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy(share.shareUrl, 'Share link')}
                    title="Copy share link"
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>

              <div className="share-actions">
                {extendingShare === share.id ? (
                  <div className="extend-form">
                    <div className="extend-input-group">
                      <input
                        type="number"
                        min="1"
                        max="720"
                        value={extendHours}
                        onChange={(e) => setExtendHours(parseInt(e.target.value) || 24)}
                        className="extend-input"
                      />
                      <span className="extend-label">hours</span>
                    </div>
                    <div className="extend-buttons">
                      <button
                        className="btn-primary btn-sm"
                        onClick={() => handleExtend(share.id)}
                        disabled={extendMutation.isLoading}
                      >
                        {extendMutation.isLoading ? 'Updating...' : 'Extend'}
                      </button>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => {
                          setExtendingShare(null);
                          setExtendHours(24);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => {
                        setExtendingShare(share.id);
                        setExtendHours(24);
                      }}
                      title="Extend expiration"
                    >
                      <Clock size={16} />
                      <span>Extend</span>
                    </button>
                    <button
                      className="btn-danger btn-sm"
                      onClick={() => handleDelete(share.id, share.credential?.name || share.kubeconfig?.name || share.vpn?.name || 'resource')}
                      title="Revoke share"
                    >
                      <Trash2 size={16} />
                      <span>Delete</span>
                    </button>
                  </>
                )}
              </div>

              {isExpiringSoon(share.expiresAt) && (
                <div className="expiring-warning">
                  <AlertCircle size={16} />
                  <span>This share is expiring soon!</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Shares;

